import React, {useEffect, useRef, useState} from 'react';
import {useHistory, Link, useParams} from 'react-router-dom';

import {Button, Dialog, DialogContent, DialogTitle, Typography} from '@mui/material';
import {isEmpty} from 'lodash';
import * as Moment from 'moment';
import {extendMoment} from 'moment-range';
import Parser from 'html-react-parser';
import {isAndroid, isFirefox, isIOS, isMobile, isSafari} from 'react-device-detect';
import slugify from 'slugify';
import Slider from 'react-slick';
import {toast} from 'react-toastify';

import Api from '../api';
import {appServices} from '../services';
import {ssrsReport} from '../services/ssrsReportService';
import {logUserAction} from '../services/appServices';
import {useStore} from '../store';
import UserActions from '../constants/user-actions';
import generateICalFile from '../helpers/timetable-ical';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import * as theme from '../theme';
import styled from 'styled-components';

// import icons
import ArrowBack from 'react-ionicons/lib/IosArrowBack';
import PreviousBackArrow from 'react-ionicons/lib/MdArrowBack';
import ArrowForward from 'react-ionicons/lib/IosArrowForward';
import CalendarIcon from 'react-ionicons/lib/MdCalendar';
import ConflictIcon from 'react-ionicons/lib/MdFlash';
import HelpIcon from 'react-ionicons/lib/MdHelpCircle';
import PrintIcon from 'react-ionicons/lib/MdPrint';
import CourseIcon from 'react-ionicons/lib/MdSchool';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {ReactComponent as AppointmentIcon} from '../assets/appointment.svg';

// import components
import Edges from '../components/Edges';
import {Instructions, TimetableCalendarSubscriptionInstructions} from '../components/Instructions';
import TimeSlotPicker from './OfficeHours/TimeSlotPicker';
import SubscribeDialog from '../components/dialog/SubscribeDialog';

const borderColor = '#f9f9f9';
const heightPerHour = 100;
const stepsPerHour = 2;

import CustomDialogTitle from '../components/dialog/DialogTitle';

const Timetable = (props) => {
    const moment = extendMoment(Moment);
    const {school, type, termSchoolID, ID} = useParams();
    let history = useHistory();

    moment.updateLocale('en', {
        week: {
            dow: 1
        }
    });

    const days = [
        {name: 'Monday', short: 'Mo'},
        {name: 'Tuesday', short: 'Tu'},
        {name: 'Wednesday', short: 'We'},
        {name: 'Thursday', short: 'Th'},
        {name: 'Friday', short: 'Fr'}
    ];

    const defaultStartTime = moment('08:30:00', 'HH:mm:ss');
    const defaultEndTime = moment('17:30:00', 'HH:mm:ss');

    const api = new Api();

    const [
        {
            appState: {timetables, schools, actives}
        },
        dispatch
    ] = useStore() as any;

    if (type && schools && termSchoolID && school) {
        const schoolObj = schools.find((o) => slugify(o.schoolName, {replacement: '-', lower: true}) === school);

        if (!schoolObj) {
            history.push('/notfound', {errorMessage: `The school ${school} is invalid`});
        }

        if (schoolObj) {
            const termObj = schoolObj.terms.find((o) => o.termSchoolID === parseInt(termSchoolID));

            if (!termObj) {
                history.push('/notfound', {errorMessage: `The term ${termSchoolID} is invalid`});
            }
        }

        if (type !== 'set' && type !== 'instructor') {
            history.push('/notfound', {errorMessage: `The type ${type} is invalid`});
        }
    }

    // Initialize week with null
    // Once we get the active school we calculate the active week
    const [activeWeek, setActiveWeek] = useState(null);
    const [view, setView] = useState(isMobile ? 'day' : 'week');
    const [activeDay, setActiveDay] = useState(0);
    const [details, setDetails] = useState({open: false, content: null, showButton: false, cellData: null});
    const [viewConflicts, setViewConflicts] = useState(true);
    const [help, setHelp] = useState(false);
    const [isCalendarSubscriptionDialogOpen, setIsCalendarSubscriptionDialogOpen] = useState(false);
    const [crn, setCrn] = useState(false);
    const [calendarDialog, setCalendarDialog] = useState({open: false, message: null});

    const [officeHourDialog, setOfficeHourDialog] = useState(false);
    const [timeSlots, setTimeSlots] = useState([]);

    const activeSchool = schools
        ? schools.find((o) => slugify(o.schoolName, {replacement: '-', lower: true}) === school)
        : null;

    const activeTerm = activeSchool ? activeSchool.terms.find((o) => o.termSchoolID === parseInt(termSchoolID)) : null;

    // Check if term is current term
    const termHasStarted = activeTerm && moment().isAfter(activeTerm.startDate);

    const [state, setState] = useState({
        filteredTimeSlots: [],
        weekTimeSlots: [],
        isWeekView: true
    });

    const slider1Ref = useRef(null);
    const slider2Ref = useRef(null);

    const timetableID = `${ID}_${termSchoolID}`;

    useEffect(() => {
        if (activeTerm) {
            // get starting week of term
            const termStartDate = moment(activeTerm.startDate, 'YYYY-MM-DD').startOf('week');
            const termStartWeek = termStartDate.week();

            // we need to calculate the weeks from the beginning, because the end date can be in a new year
            // and therefore it would start with 1 again
            const termEndDate = moment(activeTerm.endDate, 'YYYY-MM-DD').startOf('week');
            const termEndWeek = termEndDate.diff(termStartDate, 'week') + termStartWeek;

            const currentDate = moment().startOf('week');
            const currentWeek = currentDate.diff(termStartDate, 'week') + termStartWeek;

            //Default starting week
            let week = (activeTerm.termCode.endsWith('B') ? 1 : 2); // Default week: use 2 except for B-Terms;
            if (currentWeek >= termStartWeek && currentWeek <= termEndWeek + 1) {
                week = currentWeek - termStartWeek + 1;
            }

            setActiveWeek(week);
        }
    }, [activeSchool, activeTerm]);

    useEffect(() => {
        const loadTimetables = async () => {
            if (type === 'instructor') {
                await appServices.getInstructorTimetable({instructorID: ID, termSchoolID}, dispatch);
            } else if (type === 'set') {
                await appServices.getSetTimetable({setID: ID, termSchoolID}, dispatch);
            }
        };

        if (!timetables[timetableID] && activeWeek !== null) {
            loadTimetables().catch(() => toast.error('Unable to load timetable'));
        }

        setDetails({
            open: false,
            content: null,
            showButton: false,
            cellData: null
        });

    }, [activeWeek, ID, termSchoolID, type, timetableID, timetables]);

    useEffect(() => {
        const fetchTimeSlots = async () => {
            let slots = await api.get(`/api/timeslot/timeslots?instructorId=${ID}&termSchoolId=${termSchoolID}`);
            setTimeSlots(slots);
        };

        fetchTimeSlots();
    }, [ID, termSchoolID]);

    const handleIcalClick = () => {
        if (isMobile && isIOS && !isSafari) {
            setCalendarDialog({
                open: true,
                message: 'Please use the Safari Browser to download the calendar file.'
            });
        } else if (isMobile && isAndroid && isFirefox) {
            setCalendarDialog({
                open: true,
                message: 'Please use another Browser to download the calendar file.'
            });
        } else {
            generateICalFile(timetables[timetableID]);
        }

        logUserAction(UserActions.TTICAL, termSchoolID, ID, type);
    };

    const openOfficeHoursDialog = () => {
        setOfficeHourDialog(true);
    };

    const handleAppointmentDialogClose = () => {
        setOfficeHourDialog(false);
        setDetails({open: false, content: null, showButton: false, cellData: null});
    };

    /*
        - Filter the data for office hours dialog using the date 
        - Open the office hours dialog
    */
    const handleOpenOfficeHoursDialog = () => {
        const cellData = details.cellData;

        if (!cellData) return;
        if (!cellData.date) return;

        const filteredTimeSlots = timeSlots.filter((t) =>
            moment(t.date, 'YYYY-MM-DD').isSame(moment(cellData.date, 'YYYY-MM-DD'))
        );

        setOfficeHourDialog(true);

        setState({
            ...state,
            filteredTimeSlots: filteredTimeSlots,
            isWeekView: false
        });
    };

    //Update the booked timeslot in the state
    const handleTimeslotBooking = (timeSlot) => {
        const timeslots = [...timeSlots];

        const index = timeSlots.findIndex(
            (t) => t.ohId === timeSlot.ohId && t.timeSlotIndex === timeSlot.timeSlotIndex
        );

        if (index === -1) return;
        timeslots[index].isAvailable = false;

        setTimeSlots(timeslots);
    };

    const handleIcalSubscribe = async () => {
        setIsCalendarSubscriptionDialogOpen(true);
    };

    const createTimetable = () => {
        const {
            config: {minStart, maxEnd}
        } = timetables[timetableID];

        // Convert string values to dates
        const configStartTime = moment(minStart, 'HH:mm:ss');

        // We have to round up the config end time to full 30 minutes
        const remainder = 30 - (moment(maxEnd, 'HH:mm:ss').minute() % 30);
        const configEndTime =
            remainder < 30 ? moment(maxEnd, 'HH:mm:ss').add(remainder, 'minutes') : moment(maxEnd, 'HH:mm:ss');

        // Check if config values are out of default range
        const startTime = configStartTime.isBefore(defaultStartTime) ? configStartTime.clone() : defaultStartTime.clone();
        const endTime = configEndTime.isAfter(defaultEndTime) ? configEndTime.clone() : defaultEndTime.clone();

        // Get times for sidebar
        const times = [];
        const interval = 60 / stepsPerHour;
        let current = startTime.clone();
        while (current.isSameOrBefore(endTime)) {
            times.push(current.format('HH:mm'));
            current.add(interval, 'minutes');
            
            if (times.length > 100) break;
        }

        // Generate active week + date values for pagination
        const termStartDate = moment(activeTerm.startDate, 'YYYY-MM-DD');
        const termStartWeek = termStartDate.week();

        // we need to calculate the weeks from the beginning, because the end date can be in a new year
        // and therefore it would start with 1 again
        const termEndDate = moment(activeTerm.endDate, 'YYYY-MM-DD');
        const termEndWeek = termEndDate.diff(termStartDate, 'week') + termStartWeek;

        const currentWeek = termStartWeek + activeWeek;

        let week = termStartWeek;
        if (currentWeek >= termStartWeek && currentWeek <= termEndWeek + 1) {
            week = currentWeek - 1;
        }

        const activeMonthYear = moment(activeTerm.startDate, 'YYYY-MM-DD').week(week).format('MMM YYYY');

        const settings = {
            speed: 500,
            slidesToShow: view === 'day' ? 1 : 5,
            slidesToScroll: 1,
            infinite: false,
            beforeChange: (oldIndex, newIndex) => {
                setActiveDay(newIndex);

                if (slider2Ref && slider2Ref.current) {
                    slider2Ref.current.slickGoTo(newIndex);
                }
            }
        };

        const downloadSSRSReport = async () => {
            if (type === 'set') {
                await ssrsReport(1, ID, termSchoolID, activeWeek - 1, crn, viewConflicts);
            } else if (type === 'instructor') {
                await ssrsReport(2, ID, termSchoolID, activeWeek - 1, crn, viewConflicts);
            }
        };

        const renderTime = (dataItem) => {
            return (
                <p
                    style={{
                        marginBottom: '5px',
                        fontSize: isMobile && view === 'week' ? '7.5px' : isMobile && view === 'day' ? '10px' : '12.5px'
                    }}
                    children={`${moment(dataItem.timeIn, 'HH:mm:ss').format('hh:mm')} - ${moment(
                        dataItem.timeOut,
                        'HH:mm:ss'
                    ).format('hh:mm')}`}
                />
            );
        };

        const renderCourse = (dataItem) => {
            let classType = '';
            if (Array.isArray(dataItem.sections) && dataItem.sections.length > 0 && dataItem.sections[0].classType)
                classType = dataItem.sections[0].classType;

            return (
                <p
                    // style={{ fontSize: isMobile && view === "week" ? "8px" : isMobile && view === "day" ? "10px" : "13px" }}
                    children={`${dataItem.course.subject} ${dataItem.course.courseNumber} ${classType}`}
                />
            );
        };

        const renderSections = (p) => {
            const sections = [...p.sections];
            const sortedSections = sections.sort((a, b) => (a.crn > b.crn ? 1 : b.crn > a.crn ? -1 : 0));

            return sortedSections.map((q) => {
                const crnID = `(${q.crn}) ${q.enrolled}/${q.roomCap}`;
                return <p key={crnID} children={crnID}/>;
            });
        };

        const renderSets = (dataItem) => {
            let displayCode = '';

            //FIXME: This is hack as the backend returns the wrong set code for conflicts
            if (Array.isArray(dataItem.sets) && dataItem.sets.length === 1) displayCode = dataItem.sets[0].setCode;

            return (
                <p>
                    {dataItem.sets.map((set) => {
                        const isUnderlined = ID != set.setID;
                        return (
                            <span style={{textDecoration: isUnderlined ? 'underline' : 'none'}}>
                                <StyledLink
                                    key={set.setID}
                                    to={`/${slugify(activeSchool.schoolName, {
                                        replacement: '-',
                                        lower: true
                                    })}/set/${termSchoolID}/${set.setID}`}
                                    children={displayCode || set.displayCode}
                                />
                            </span>
                        );
                    })}
                </p>
            );
        };

        const renderInstructors = (dataItem) => {
            const instructors = [...dataItem.instructors];

            const sortedInstructors = instructors.sort((a, b) =>
                a.name.localeCompare(b.name, 'en-US', {
                    sensitivity: 'base',
                    ignorePunctuation: true
                })
            );

            return (
                <p>
                    {sortedInstructors.map((instructor) => {
                        if (instructor === null) return null;
                        const isUnderlined = ID != instructor.instructorID;
                        return (
                            <span style={{textDecoration: isUnderlined ? 'underline' : 'none'}}>
                                <StyledLink
                                    key={instructor.instructorID}
                                    to={`/${slugify(activeSchool.schoolName, {
                                        replacement: '-',
                                        lower: true
                                    })}/instructor/${termSchoolID}/${instructor.instructorID}`}
                                    children={Parser(instructor.firstName + ' ' + instructor.lastName + ' ')}
                                />
                            </span>
                        );
                    })}
                </p>
            );
        };

        const addThreeHourBreak = (day, activeSchool, dailyTimetable) => {
            // Add specialty three hours break for business school
            const breakStart = moment('14:30:00', 'HH:mm:ss');
            const breakEnd = moment('17:20:00', 'HH:mm:ss');
            const breakRange = moment.range(breakStart, breakEnd);

            const result = dailyTimetable.find((q) => {
                const courseTimeIn = moment(q.timeIn, 'HH:mm:ss');
                const courseTimeOut = moment(q.timeOut, 'HH:mm:ss');
                const courseRange = moment.range(courseTimeIn, courseTimeOut);

                return breakRange.overlaps(courseRange);
            });

            if (day.name === 'Wednesday' && activeSchool.schoolName === 'Business' && !result) {
                dailyTimetable.push({
                    id: 'break',
                    course: {courseNumber: '', subject: 'Three-Hour Break'},
                    timeIn: breakStart.format('HH:mm:ss'),
                    timeOut: breakEnd.format('HH:mm:ss')
                });
            }
        };

        const getContent = (data) => {
            const date = data.date;
            const content = [data];

            if (data.isOfficeHour && data.isOfficeHourAppointment) {
                return (
                    <>
                        {renderTime(data)}
                        <p children={Parser(data.officeHourNote || '')}/>
                    </>
                );
            }

            if (data.isOfficeHour && !data.isOfficeHourAppointment) {
                return (
                    <>
                        {renderTime(data)}
                        <p children={Parser(data.officeHourNote || '')}/>
                    </>
                );
            }

            if (data.isEvent) {
                return (
                    <>
                        {/* TimeIn and TimeOut are Strings */}
                        {data.timeIn != minStart || (data.timeOut != maxEnd && renderTime(data))}
                        <p children={Parser(data.note || '')}/>
                        {/* TODO: Add to API */}
                        {/* {section.room && (
							<section children={`${section.buildingCode} ${section.room.roomNumber}`} />
						)} */}
                    </>
                );
            }

            return <div>{content.map((s, index) => renderClassData(s, index))}</div>;
        };

        const renderClassData = (cellData, index) => {
            return (
                <>
                    {renderTime(cellData)}
                    {cellData.course && renderCourse(cellData)}

                    {cellData.room && <p children={`${cellData.buildingCode} ${cellData.room.roomNumber}`}/>}

                    {cellData.sets && renderSets(cellData)}
                    {cellData.instructors && renderInstructors(cellData)}

                    {crn && cellData.sections && renderSections(cellData)}
                </>
            );
        };

        const renderOfficeHoursHeader = (activeWeekTimetables) => {
            if (type !== 'instructor') return null;

            if (!Array.isArray(activeWeekTimetables)) return null;

            const hasOfficeHourAppointments = activeWeekTimetables.some((t) => t.isOfficeHourAppointment);

            if (!hasOfficeHourAppointments) return null;

            return (
                <>
                    <OfficeHourLegend>
                        <Legend>
                            <svg
                                width='21'
                                height='19'
                                viewBox='0 0 21 19'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <rect width='20' height='20' fill='#32b11e'/>
                            </svg>
                            Appointment based office hours
                        </Legend>
                        <Legend>
                            <svg
                                width='21'
                                height='19'
                                viewBox='0 0 21 19'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <rect width='20' height='20' fill='#009688'/>
                            </svg>
                            Drop-in office hours
                        </Legend>
                        <OfficeHoursButtonContainer>
                            <StyledButton
                                style={{
                                    background: '#005FA5',
                                    color: '#FFFFFF',
                                    padding: '10px 20px',
                                    margin: '0 0 0 1em'
                                }}
                                onClick={() => {
                                    openOfficeHoursDialog();
                                    setState({
                                        ...state,
                                        isWeekView: true
                                    });
                                }}
                            >
                                Book Office Hours
                            </StyledButton>
                        </OfficeHoursButtonContainer>
                    </OfficeHourLegend>
                </>
            );
        };
        // @ts-ignore
        // @ts-ignore
        // @ts-ignore
        return (
            <Container>
                <PreviousBackArrow
                    style={{width: 25, height: 25, cursor: 'pointer'}}
                    // go back to previous page
                    onClick={() => {
                        if (actives.term) {
                            history.push(
                                `/${school}#${actives.tab === 1 ? `set${actives.set}` : actives.instructor}`
                            );
                        }
                    }}
                />
                {timetables[timetableID] && timetables[timetableID].header && timetables[timetableID].header.mainTitle && (
                    <TimetableInfo>
                        <Headline children={`${Parser(timetables[timetableID].header.mainTitle)} - BCIT Timetable`}/>
                        <TimetableInfoContent>
                            <TimetableInfoColumn>
                                {/* Set timetables do not have subTitle and InstructorInfo */}
                                {timetables[timetableID].header.subTitle && (
                                    <SubTitle children={Parser(timetables[timetableID].header.subTitle)}/>
                                )}
                                {/* //FIXME: refactor */}
                                {timetables[timetableID].header.instructorInfo &&
                                    timetables[timetableID].header.instructorInfo.split('|').map((info, index) => {
                                        if (info.includes('Email')) {
                                            const email = info.split(': ')[1].replace(/[ ]+/, '');
                                            if (email.includes('N/A')) {
                                                return <SubTitle key={index} children={Parser(info)}/>;
                                            } else {
                                                return (
                                                    <SubTitle key={index}>
                                                        Email: <a href={`mailto:${email}`}>{email}</a>{' '}
                                                    </SubTitle>
                                                );
                                            }
                                        } else {
                                            return <SubTitle key={index} children={Parser(info)}/>;
                                        }
                                    })}
                                {timetables[timetableID].isDraft && !termHasStarted && (
                                    <DraftNotice children={'This is a draft timetable'}/>
                                )}
                            </TimetableInfoColumn>

                            <TimetableInfoColumn>
                                {timetables[timetableID].header.publicNote && (
                                    <SubTitle>
                                        {' '}
                                        <strong>Note:</strong> {Parser(timetables[timetableID].header.publicNote)}{' '}
                                    </SubTitle>
                                )}
                            </TimetableInfoColumn>
                        </TimetableInfoContent>
                    </TimetableInfo>
                )}
                {renderOfficeHoursHeader(timetables[timetableID].timetablesByWeek[activeWeek])}
                <TimetableHeader>
                    <WeekInformation>
                        <ArrowBack
                            onClick={() => (currentWeek > termStartWeek + 1 ? setActiveWeek(activeWeek - 1) : {})}
                            color={currentWeek > termStartWeek + 1 ? theme.colors.secondary : '#ccc'}
                        />
                        <CurrentWeek>
                            {activeMonthYear}, Week {activeWeek}
                        </CurrentWeek>
                        <ArrowForward
                            onClick={() => (currentWeek <= termEndWeek ? setActiveWeek(activeWeek + 1) : {})}
                            color={currentWeek <= termEndWeek ? theme.colors.secondary : '#ccc'}
                        />
                    </WeekInformation>
                    <div>
                        {/* Toggle Week and Day */}
                        <ViewToogle>
                            <View onClick={() => setView('week')} children='Week' active={view === 'week'}/>
                            <View onClick={() => setView('day')} children='Day' active={view === 'day'}/>
                        </ViewToogle>
                    </div>
                </TimetableHeader>

                <DayPagination>
                    {days.map((o, i) => {
                        //TODD: Account for term ending in the year after
                        const year = moment(termStartDate).year();
                        return (
                            <Day
                                key={o.short}
                                onClick={() => {
                                    if (slider1Ref && slider1Ref.current) {
                                        slider1Ref.current.slickGoTo(i);
                                    }
                                    setActiveDay(i);
                                }}
                                active={i === activeDay}
                                view={view}
                            >
                                {o.short} <br/>
                                {moment(termStartDate)
                                    .year(year)
                                    .week(week)
                                    .startOf('isoWeek')
                                    .day(o.name)
                                    .format('DD')}
                            </Day>
                        );
                    })}
                </DayPagination>

                <TimetableContainer>
                    <Sidebar>
                        {times.map((time, index) => (
                            <TimeUnit key={`${time}-${index}`} showLine={!time.endsWith(':30')}>
                                <span>{time}</span>
                            </TimeUnit>
                        ))}
                    </Sidebar>

                    <TimeTable hours={times.length / stepsPerHour}>
                        <Slider ref={slider1Ref} {...settings}>
                            {days.map((o) => {
                                const dailyTimetable = timetables[timetableID].timetablesByWeek[activeWeek]
                                    ? timetables[timetableID].timetablesByWeek[activeWeek].filter(
                                        (p) => p.dayOfWeek === o.name
                                    )
                                    : [];

                                addThreeHourBreak(o, activeSchool, dailyTimetable);

                                return (
                                    <div key={o.name}>
                                        {dailyTimetable.map((p) => {
                                            const y = moment(p.timeIn, 'HH:mm:ss').diff(startTime, 'minutes') / 60;

                                            // We have to round the end time to 30-minutes
                                            const remainder = 30 - (moment(p.timeOut, 'HH:mm:ss').minute() % 30);

                                            // We have to make sure that we don't round even numbers up as well
                                            const roundedTimeOut =
                                                remainder < 30
                                                    ? moment(p.timeOut, 'HH:mm:ss')
                                                        .add(remainder, 'minutes')
                                                        .format('HH:mm:ss')
                                                    : moment(p.timeOut, 'HH:mm:ss');
                                            const height =
                                                moment(roundedTimeOut, 'HH:mm:ss').diff(startTime, 'minutes') / 60 - y;

                                            let backgroundColor = '';
                                            let textColor = '#fff';

                                            let button = false;
                                            let textAlign = false;

                                            if (p.isOfficeHour && p.isOfficeHourAppointment) {
                                                backgroundColor = '#32b11e';
                                                textColor = '#fff';
                                                button = true;
                                                textAlign = true;
                                            } else if (p.isOfficeHour && !p.isOfficeHourAppointment) {
                                                backgroundColor = '#009688';
                                            } else if (p.id === 'break' || p.isEvent) {
                                                backgroundColor = borderColor;
                                                textColor = '#333';
                                            } else if (p.conflict.anyConflict && viewConflicts) {
                                                backgroundColor = theme.colors.highlight;
                                                textColor = '#333';
                                            }

                                            let totalSlots = timeSlots.filter((s) => {
                                                return (
                                                    moment(s.date).isSame(moment(p.date)) &&
                                                    moment(s.timeIn, 'HH:mm').isSameOrAfter(
                                                        moment(p.timeIn, 'HH:mm')
                                                    ) &&
                                                    moment(p.timeOut, 'HH:mm').isSameOrAfter(moment(s.timeOut, 'HH:mm'))
                                                );
                                            });

                                            let availableSlots = [];

                                            if (p.isOfficeHourAppointment && p.isOfficeHour) {
                                                availableSlots = totalSlots.filter((s) => s.isAvailable);

                                                if (availableSlots.length === 0) {
                                                    backgroundColor = 'grey';
                                                }
                                            }

                                            const content = getContent(p);

                                            return (
                                                <Content
                                                    key={p.id}
                                                    y={y}
                                                    height={height}
                                                    view={view}
                                                    backgroundColor={backgroundColor}
                                                    textColor={textColor}
                                                    isMobile={isMobile}
                                                    textAlign={textAlign}
                                                    onClick={(e) => {
                                                        // view === 'week' &&
                                                        e.target.tagName.toLowerCase() !== 'a' &&
                                                        setDetails({
                                                            open: true,
                                                            content: content,
                                                            showButton: button,
                                                            cellData: p
                                                        });
                                                    }}
                                                    style={{cursor: 'pointer'}}
                                                >
                                                    {content}
                                                    <p>
                                                        {p.isOfficeHourAppointment && p.isOfficeHour && `${availableSlots.length}/${totalSlots.length} available`}
                                                    </p>
                                                </Content>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </Slider>
                    </TimeTable>
                </TimetableContainer>

                {viewConflicts && !isEmpty(timetables[timetableID].conflictsByWeek[activeWeek]) && (
                    <ConflictTimetableContainer>
                        <TimetableContainer>
                            <Sidebar/>
                            <ConflictHeader>
                                <Typography children='Conflicts'/>
                            </ConflictHeader>
                        </TimetableContainer>

                        <TimetableContainer>
                            <Sidebar/>
                            <ConflictTimeTable hours={times.length / stepsPerHour}>
                                <Slider ref={slider2Ref} {...settings} draggable={false}>
                                    {days.map((o) => {
                                        const dailyTimetable = timetables[timetableID].conflictsByWeek[activeWeek]
                                            ? timetables[timetableID].conflictsByWeek[activeWeek].filter(
                                                (p) => p.dayOfWeek === o.name
                                            )
                                            : [];
                                        return (
                                            <div key={o.name}>
                                                {dailyTimetable.map((p) => {
                                                    const classContent = getContent(p);
                                                    return (
                                                        <Conflict
                                                            key={p.id}
                                                            view={view}
                                                            onClick={(e) =>
                                                                view === 'week' &&
                                                                e.target.tagName.toLowerCase() !== 'a' &&
                                                                setDetails({
                                                                    open: true,
                                                                    content: classContent,
                                                                    showButton: false,
                                                                    cellData: null
                                                                })
                                                            }
                                                            children={classContent}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </Slider>
                            </ConflictTimeTable>
                        </TimetableContainer>
                    </ConflictTimetableContainer>
                )}

                <TimetableFooter>
                    {activeTerm && activeTerm.isCalendarOpen && (
                        <Button
                            variant='outlined'
                            startIcon={<CalendarIcon color={theme.colors.secondary}/>}
                            onClick={handleIcalSubscribe}
                            children='Subscribe iCal'
                            color='secondary'
                        />
                    )}

                    {activeTerm && activeTerm.isCalendarOpen && (
                        <Button
                            variant='outlined'
                            startIcon={<CalendarIcon color={theme.colors.secondary}/>}
                            onClick={handleIcalClick}
                            children='Download iCal'
                            color='secondary'
                        />
                    )}

                    {!isMobile && (
                        <Button
                            variant='outlined'
                            startIcon={<PrintIcon color={theme.colors.secondary}/>}
                            onClick={() => downloadSSRSReport()}
                            children='Print'
                            color='secondary'
                        />
                    )}

                    <Button
                        variant='outlined'
                        startIcon={<HelpIcon color={theme.colors.secondary}/>}
                        onClick={() => setHelp(true)}
                        children='Help'
                        color='secondary'
                    />

                    <Button //CRNButton to hide on mobile
                        variant='outlined'
                        startIcon={<CourseIcon color={theme.colors.secondary}/>}
                        onClick={() => setCrn(!crn)}
                        children={crn ? 'Hide CRN' : 'Show CRN'}
                        color='secondary'
                    />

                    {!isEmpty(timetables[timetableID].conflictsByWeek) && (
                        <Button
                            variant='outlined'
                            startIcon={<ConflictIcon color={theme.colors.secondary}/>}
                            onClick={() => setViewConflicts(!viewConflicts)}
                            children={viewConflicts ? 'Hide Conflicts' : 'Show Conflicts'}
                            color='secondary'
                        />
                    )}
                </TimetableFooter>

                <Dialog open={help} onClose={() => setHelp(false)}>
                    <CustomDialogTitle id='reading-timetable-dialog-title' onClose={() => setHelp(false)}>
                        Timetable anatomy
                    </CustomDialogTitle>
                    <StyledDialogContent>
                        <Instructions/>
                    </StyledDialogContent>
                </Dialog>


                <SubscribeDialog
                    open={isCalendarSubscriptionDialogOpen}
                    onClose={() => setIsCalendarSubscriptionDialogOpen(false)}
                    termSchoolId={termSchoolID}
                    timetableId={ID}
                    timetableType={type}
                    title={Parser(timetables[timetableID].header.mainTitle)}
                    isMobile={isMobile}
                />

                <Dialog open={calendarDialog.open} onClose={() => setCalendarDialog({open: false, message: null})}>
                    <CustomDialogTitle
                        id='message-dialog-title'
                        onClose={() => setCalendarDialog({open: false, message: null})}
                    >
                        Message
                    </CustomDialogTitle>
                    <StyledDialogContent>{calendarDialog.message}</StyledDialogContent>
                </Dialog>

                {/* DIALOG BOX - CALENDAR/SCHEDULE INFO POP UP */}
                {details.open && (
                    <Dialog
                        onClose={() => setDetails({open: false, content: null, showButton: false, cellData: null})}
                        aria-labelledby='course-details'
                        open={details.open}
                        maxWidth='sm'
                        fullWidth
                    >
                        <DetailsTitle id='course-details'>
                            Details
                            <CloseIcon
                                onClick={() =>
                                    setDetails({open: false, content: null, showButton: false, cellData: null})
                                }
                                style={{
                                    position: 'absolute',
                                    margin: '15px',
                                    top: 0,
                                    right: 0,
                                    color: '#747474',
                                    cursor: 'pointer'
                                }}
                            />
                        </DetailsTitle>
                        <StyledDialogContent children={
                            <>
                                {!details?.cellData?.isOfficeHourAppointment ? (
                                    <>
                                        {details.content}
                                    </>

                                ) : (
                                    <>
                                        {renderTime(details.cellData)}
                                        <div>{Parser(details.cellData.officeHourNote || '')} </div>
                                        <div>{details.cellData && details.cellData.isOfficeHourAppointmentOnline ? 'Online appointment (unless instructor indicates otherwise)' : 'In-Person appointment (unless instructor indicates otherwise)'} </div>
                                    </>
                                )}
                            </>
                        }/>
                        {/* If user clicks on an appointment, show a "book office hours" button */}
                        {/* TODO: The button should be part of the details.content */}
                        {details?.cellData?.isOfficeHourAppointment && (
                            <OfficeHoursModalContainer>
                                <StyledButton
                                    style={{
                                        background: '#005FA5',
                                        color: '#FFFFFF',
                                        padding: '10px 20px'
                                    }}
                                    onClick={handleOpenOfficeHoursDialog}
                                >
                                    Book Office Hours
                                </StyledButton>
                            </OfficeHoursModalContainer>
                        )}
                    </Dialog>
                )}
            </Container>
        );
    };

    return <>
        {timetables[timetableID] && activeTerm && (
            <Edges size='md'>
                {timetables[timetableID].header === null ? (
                    <>
                        <InvalidTimetableMessage>
                            The timetable id {timetableID.substring(0, timetableID.indexOf('_'))} is either not
                            valid or not open yet for {activeTerm?.label ?? 'the selected term'}.
                        </InvalidTimetableMessage>
                        <InvalidTimetableMessage>
                            {' '}
                            <a href='/'>Click here</a> to return to the main menu.
                        </InvalidTimetableMessage>
                    </>
                ) : (
                    createTimetable()
                )}
            </Edges>
        )}
        {/* TODO: Code clean up */}
        {/* DIALOG BOX - OFFICE HOUR APPOINTMENT STEPS */}
        {timetables[timetableID] && timetables[timetableID].header && timetables[timetableID].header.mainTitle && (
            <Dialog
                onClose={handleAppointmentDialogClose}
                aria-labelledby='simple-dialog-title'
                open={officeHourDialog}
                fullWidth
            >
                <StyledDialogHeader>
                    <OfficeHourDialogTitle children={'Office Hour Appointment'} color='primary'/>
                    <IconButton
                        style={{position: 'absolute', top: 0, right: 0}}
                        onClick={handleAppointmentDialogClose}
                        size="large">
                        <CloseIconStyled/>
                    </IconButton>
                </StyledDialogHeader>
                <DialogContent dividers>
                    <TimeSlotPicker
                        instructorInfo={timetables[timetableID].header.mainTitle}
                        instructorId={ID}
                        termSchoolID={termSchoolID}
                        activeWeek={activeWeek}
                        termStartDate={activeTerm.startDate}
                        termEndDate={activeTerm.endDate}
                        closeDialog={handleAppointmentDialogClose}
                        // Only display the timeslots for the selected day and display entire week for the Book Office Hours button (on the page)
                        bookingTimeSlots={state.isWeekView ? timeSlots : state.filteredTimeSlots}
                        isWeekView={state.isWeekView}
                        onTimeslotBooking={handleTimeslotBooking}
                    />
                </DialogContent>
            </Dialog>
        )}
    </>;
};

const StyledButton = styled(Button)`
    && {
        &:hover {
            opacity: 0.8;
        }

        @media (max-width: 640px) {
            font-size: 12px;
        }
    }
`;

const InvalidTimetableMessage = styled.p`
    color: ${theme.colors.primary};
    font-size: 20px;
    padding-top: 10px;
    text-align: center;
`;

const Container = styled.div`
    overflow: hidden;
    /* padding-bottom: 10px; */
    padding: 40px 0;
`;

const TimetableInfo = styled.div`
    flex: 1;
    background: #fff;
    margin-bottom: 20px;
    padding: 20px;
    border: 1px solid ${borderColor};

    @media (min-width: 640px) {
        margin-left: 50px;
    }

    @media (max-width: 340px) {
        margin-bottom: 10px;
    }
`;

const OfficeHourLegend = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex-wrap: wrap;
    background: #fff;
    margin-bottom: 20px;
    padding: 20px;
    border: 1px solid ${borderColor};

    div svg {
        margin: 10px 10px;
    }

    @media (min-width: 640px) {
        flex-direction: row;
        align-items: center;
        margin-left: 50px;
        div svg {
            margin-right: 10px;
            margin-left: 10px;
        }
    }

    @media (max-width: 340px) {
        padding: 10px;
    }
`;

const Legend = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 5px;

    @media (max-width: 340px) {
        font-size: 12px;
    }

    p {
        margin-left: 1em;
    }
`;

const OfficeHoursButtonContainer = styled.div`
    @media (min-width: 768px) {
        margin-left: auto;
        order: 2;
    }

    @media (max-width: 768px) {
        margin-top: 10px;
    }
`;
const OfficeHoursModalContainer = styled.div`
    margin: 0 0 20px 20px;
`;

const TimetableInfoContent = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const TimetableInfoColumn = styled.div`
    width: 100%;

    @media (min-width: 640px) {
        width: calc(50% - 20px);
    }
`;

const Headline = styled.h2`
    color: ${theme.colors.primary};
    margin-top: 0;
    margin-bottom: 0;
    @media (max-width: 340px) {
        font-size: 18px;
    }
`;

const SubTitle = styled.p`
    color: #000;
    @media (max-width: 340px) {
        font-size: 12px;
    }
`;

const DraftNotice = styled.p`
    display: inline-block;
    color: #fff;
    background: #148de3;
        /* color: ${theme.colors.primary};
    background: ${theme.colors.highlight}; */
    text-align: center;
    padding: 8px 20px;
    margin: 10px 0 0;
    font-weight: 700;
`;

const TimetableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    flex: 1;
    margin-bottom: 20px;

    @media (min-width: 640px) {
        margin-left: 50px;
    }
`;

const WeekInformation = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: center;

    svg {
        cursor: pointer;
    }
`;

const CurrentWeek = styled.div`
    padding: 0 10px;
    font-size: 14px;

    @media (min-width: 640px) {
        padding: 0 20px;
    }

    @media (max-width: 340px) {
        font-size: 12px;
    }
`;

const ViewToogle = styled.div`
    display: flex;
`;

const View = styled('div' as any)`
    padding: 4px 15px;
    cursor: pointer;
    font-size: 14px;
    padding: 5px 16px;
    cursor: pointer;
    border: 1px solid ${theme.colors.secondary};
    border-radius: 20px;
    background: ${(props) => (props.active ? theme.colors.secondary : '#FFF')};
    color: ${(props) => (props.active ? '#fff' : theme.colors.secondary)};
    margin-left: 10px;
`;

const DayPagination = styled.div`
    display: flex;
    margin-left: 38px;
    background: #fff;
    border-left: 1px solid ${borderColor};
    border-right: 1px solid ${borderColor};
    border-top: 1px solid ${borderColor};

    @media (min-width: 640px) {
        margin-left: 50px;
    }
`;

const Day = styled('div' as any)`
    position: relative;
    z-index: 1;
    width: 20%;
    height: ${heightPerHour / 2}px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    cursor: pointer;
    font-size: 14px;
    line-height: 14px;
    ${(props) => props.active && props.view === 'day' && 'color: #fff;'};

    &:before {
        content: '';
        position: absolute;
        z-index: -1;
        left: 50%;
        top: 3px;
        height: 44px;
        width: 44px;
        transform: translateX(-50%);
        border-radius: 50%;
        background: ${theme.colors.secondary};
        opacity: ${(props) => (props.active && props.view === 'day' ? 1 : 0)};
    }
`;

const TimetableContainer = styled.div`
    display: flex;
`;

const TimeTable = styled('div' as any)`
    width: calc(100% - 38px);

    @media (min-width: 640px) {
        width: calc(100% - 50px);
    }

    .slick-slider {
        width: 100%;
        background: #fff;
        border-top: 1px solid ${borderColor};
        border-bottom: 1px solid ${borderColor};
    }

    .slick-list {
        z-index: 2;
    }

    .slick-track {
        > div:last-child {
            border-right: 1px solid ${borderColor};
        }
    }

    .slick-slide {
        position: relative;
        border-left: 1px solid ${borderColor};
        height: ${(props) => props.hours * heightPerHour - heightPerHour / 2}px;

        > div > div {
            display: block !important;
        }
    }
`;

const Sidebar = styled.div`
    width: 38px;

    @media (min-width: 640px) {
        width: 50px;
    }
`;

const TimeUnit = styled('div' as any)`
    position: relative;
    display: flex;
    justify-content: center;
    height: ${heightPerHour / stepsPerHour}px;

    span {
        position: absolute;
        z-index: 0;
        right: 5px;
        top: -6px;
        font-size: 11px;
    }

    &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 100%;
        top: 0;
        width: 100vw;
        height: 1px;
        background: ${borderColor};
        opacity: ${(props) => (props.showLine ? 0.3 : 1)};
    }
`;

const Content = styled('div' as any)`
    overflow: auto;
    position: absolute;
    left: 0;
    top: ${(props) => props.y * heightPerHour}px;
    width: 100%;
    height: calc(${(props) => props.height * heightPerHour}px - 1px);
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: ${(props) =>
            props.height > 1 || (props.isMobile && props.view === 'day') || props.textAlign ? 'center' : null};
    background: ${(props) => (props.backgroundColor ? props.backgroundColor : theme.colors.secondary)};
    padding: ${(props) => (props.height > 1 ? '10px' : '5px 0px')};
    text-align: center;

    @media (max-width: 640px) {
        padding: 5px 2px;
    }

    > div {
        max-width: 100%;
    }

    p {
        margin-top: 0;
        margin-bottom: 0;
        font-size: 11.5px;
        line-height: 14px;
        color: ${(props) => props.textColor};

        @media (max-width: 640px) {
            ${(props) =>
                    props.view === 'week' &&
                    `
                font-size: 8px;
                line-height: 1.25;
            `}
            ${(props) =>
                    props.view === 'day' &&
                    `
                font-size: 10px;
                line-height: 1.25;
            `}
        }

        @media (max-width: 340px) {
            ${(props) =>
                    props.view === 'week' &&
                    `
                font-size: 7px;
                line-height: 1.25;
            `}
            ${(props) =>
                    props.view === 'day' &&
                    `
                font-size: 9px;
                line-height: 1.25;

            `}
        }

        ${(props) =>
                props.view === 'day' &&
                `
            font-size: 11.5px;
            line-height: 14px;
        `}
    }
`;

const StyledLink = styled(Link)`
    color: ${theme.colors.primary};
    margin: 0 2px;
    text-decoration: none;
    color: inherit;
`;

const TimetableFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 1em;

    button {
        margin: 0 10px 10px;

        @media (min-width: 830px) {
            margin: 0 20px;
        }
    }
`;
const DetailsTitle = styled(DialogTitle)`
    && {
        color: #005fa5;
        border-bottom: 1px solid #dbdbdb;
        margin-bottom: 1em;
    }
`;

const StyledDialogContent = styled(DialogContent as any)`
    && {
        padding-bottom: 20px;

        p {
            margin-top: 0;
            font-size: 16px;
            line-height: 18px;
        }

        a {
            color: ${theme.colors.primary};
        }
    }
`;

const ConflictTimetableContainer = styled.div`
    margin-top: -${heightPerHour / 2}px;
    margin-bottom: ${heightPerHour / 2}px;
`;

const ConflictTimeTable = styled('div' as any)`
    width: calc(100% - 38px);
    border-right: 1px solid ${borderColor};
    border-left: 1px solid ${borderColor};

    @media (min-width: 640px) {
        width: calc(100% - 50px);
    }

    .slick-slider {
        width: 100%;
        background: #fff;
        border-top: 1px solid ${borderColor};
        border-bottom: 1px solid ${borderColor};
    }

    .slick-list {
        z-index: 2;
    }

    .slick-track {
        > div:last-child {
        }
    }

    .slick-slide {
        position: relative;

        > div > div {
            display: block !important;
        }
    }
`;

const ConflictHeader = styled.div`
    flex: 1;
    background: ${theme.colors.primary};
    padding: 10px 20px;
    display: flex;
    justify-content: center;
    color: #fff;
`;

const Conflict = styled('div' as any)`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.colors.highlight};
    padding: 10px;
    text-align: center;
    overflow: hidden;
    border: 1px solid #fff;

    @media (max-width: 640px) {
        padding: 5px 2px;
    }

    > div {
        max-width: 100%;
    }

    p {
        margin-top: 0;
        margin-bottom: 0;
        font-size: 11.5px;
        line-height: 15px;
        color: #333;

        @media (max-width: 640px) {
            ${(props) => props.view === 'week' && ` font-size: 8px; line-height: 1.25; `}
        }

        @media (max-width: 340px) {
            ${(props) =>
                    props.view === 'week' &&
                    `
                font-size: 7px;
                line-height: 1.25;
            `}
            ${(props) =>
                    props.view === 'day' &&
                    `
                font-size: 9px;
                line-height: 1.25;

            `}
        }

        ${(props) => props.view === 'day' && ` font-size: 11.5px; line-height: 1.25; `}
    }
`;

const StyledDialogHeader = styled(DialogTitle)`
    && {
        background: ${theme.colors.primary};
        display: flex;
        flex-direction: row;
        justify-content: center;
        padding: 20px 5px;

        @media (max-width: 768px) {
            padding: 15px 5px;
        }
    }
`;

const OfficeHourDialogTitle = styled(Typography)`
    && {
        color: white;
        text-align: center;
        font-weight: bold;
        text-transform: uppercase;
        font-size: 18px;
        display: inline-block;

        @media (max-width: 768px) {
            font-size: 15px;
        }

        @media (max-width: 320px) {
            font-size: 13px;
        }
    }
`;

const CloseIconStyled = styled(CloseIcon)`
    && {
        path {
            fill: #fff;
        }
    }
`;

const StyledAppointmentIcon = styled(AppointmentIcon)`
    font-size: 16px !important;
    margin-right: 3px;
`;

const FlexPTag = styled.p`
    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
    }
`;

export default Timetable;
